import { db } from './config';
import { 
  collection, 
  addDoc, 
  doc, 
  updateDoc, 
  getDoc, 
  getDocs,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { addNotification } from '@/services/notificationService';
import { notifyCommentMentions } from '@/services/commentMentionService';
import type { Comment, CommentInput } from '@/types/comments';

export const addComment = async (postId: string, comment: CommentInput & { userId: string; userName: string; userPhoto?: string }): Promise<Comment[]> => {
  try {
    const postRef = doc(db, 'posts', postId);
    const postDoc = await getDoc(postRef);
    
    if (!postDoc.exists()) {
      throw new Error('Post not found');
    }

    const newComment: any = {
      id: crypto.randomUUID(),
      content: comment.content,
      userId: comment.userId,
      userName: comment.userName,
      userPhoto: comment.userPhoto,
      timestamp: new Date().toISOString(),
      reactions: [],
      replies: []
    };

    // Add media fields only if they exist
    if (comment.mediaUrl) {
      newComment.mediaUrl = comment.mediaUrl;
    }
    if (comment.mediaType) {
      newComment.mediaType = comment.mediaType;
    }

    const post = postDoc.data();
    const comments = [...(post.comments || []), newComment];

    // Clean the comment object to remove any undefined values
    const cleanedComments = comments.map(c =>
      Object.fromEntries(
        Object.entries(c).filter(([_, value]) => value !== undefined)
      )
    );

    await updateDoc(postRef, { comments: cleanedComments });

    // Create notification for post author if it's not their own comment
    if (post.authorId && post.authorId !== comment.userId) {
      try {
        await addNotification(post.authorId, 'new_comment', {
          postId: postId,
          commentId: newComment.id,
          commenterName: comment.userName,
          commenterPhoto: comment.userPhoto,
          commentText: comment.content,
          postMediaType: post.mediaType || post.media?.[0] ? 'photo' : undefined,
          actionUrl: `/post/${encodeURIComponent(postId)}?comment=${encodeURIComponent(newComment.id)}`
        });
        console.log('✅ Comment notification created for user:', post.authorId);
      } catch (notificationError) {
        console.error('Error creating comment notification:', notificationError);
        // Don't fail the comment creation if notification fails
      }
    }

    try {
      await notifyCommentMentions({
        actorUserId: comment.userId,
        actorName: comment.userName,
        actorPhoto: comment.userPhoto,
        commentText: comment.content,
        postId,
        commentId: newComment.id,
        actionUrl: `/post/${encodeURIComponent(postId)}?comment=${encodeURIComponent(newComment.id)}`,
      });
    } catch (mentionError) {
      console.error('Error creating mention notifications:', mentionError);
    }

    return comments;
  } catch (error) {
    console.error('Error adding comment:', error);
    throw error;
  }
};

export const editComment = async (
  postId: string, 
  commentId: string, 
  content: string,
  mediaUrl?: string,
  mediaType?: string
): Promise<Comment[]> => {
  try {
    const postRef = doc(db, 'posts', postId);
    const postDoc = await getDoc(postRef);
    
    if (!postDoc.exists()) {
      throw new Error('Post not found');
    }

    const post = postDoc.data();
    const comments = post.comments.map((comment: Comment) => {
      if (comment.id === commentId) {
        return {
          ...comment,
          content,
          mediaUrl,
          mediaType,
          edited: true,
          editedAt: new Date().toISOString()
        };
      }
      return comment;
    });

    await updateDoc(postRef, { comments });
    return comments;
  } catch (error) {
    console.error('Error editing comment:', error);
    throw error;
  }
};

export const deleteComment = async (postId: string, commentId: string): Promise<Comment[]> => {
  try {
    const postRef = doc(db, 'posts', postId);
    const postDoc = await getDoc(postRef);
    
    if (!postDoc.exists()) {
      throw new Error('Post not found');
    }

    const post = postDoc.data();
    const comments = post.comments.filter((comment: Comment) => comment.id !== commentId);
    
    await updateDoc(postRef, { comments });
    return comments;
  } catch (error) {
    console.error('Error deleting comment:', error);
    throw error;
  }
};

export const addReply = async (
  postId: string, 
  commentId: string, 
  reply: CommentInput & { userId: string; userName: string; userPhoto?: string }
): Promise<Comment[]> => {
  try {
    const postRef = doc(db, 'posts', postId);
    const postDoc = await getDoc(postRef);
    
    if (!postDoc.exists()) {
      throw new Error('Post not found');
    }

    const post = postDoc.data();
    const comments = post.comments.map((comment: Comment) => {
      if (comment.id === commentId) {
        const newReply: any = {
          id: crypto.randomUUID(),
          content: reply.content,
          userId: reply.userId,
          userName: reply.userName,
          userPhoto: reply.userPhoto,
          timestamp: new Date().toISOString(),
          reactions: [],
          replies: []
        };
        
        // Add media fields only if they exist
        if (reply.mediaUrl) {
          newReply.mediaUrl = reply.mediaUrl;
        }
        if (reply.mediaType) {
          newReply.mediaType = reply.mediaType;
        }
        
        return {
          ...comment,
          replies: [...(comment.replies || []), newReply]
        };
      }
      return comment;
    });

    await updateDoc(postRef, { comments });

    // Create notification for original comment author if it's not their own reply
    const originalComment = post.comments.find((c: Comment) => c.id === commentId);
    if (originalComment && originalComment.userId && originalComment.userId !== reply.userId) {
      try {
        await addNotification(originalComment.userId, 'comment_reply', {
          postId: postId,
          commentId: commentId,
          commenterName: reply.userName,
          commenterPhoto: reply.userPhoto,
          commentText: reply.content,
          postMediaType: post.mediaType || post.media?.[0] ? 'photo' : undefined,
          actionUrl: `/post/${encodeURIComponent(postId)}?comment=${encodeURIComponent(commentId)}`
        });
        console.log('✅ Reply notification created for user:', originalComment.userId);
      } catch (notificationError) {
        console.error('Error creating reply notification:', notificationError);
        // Don't fail the reply creation if notification fails
      }
    }

    try {
      await notifyCommentMentions({
        actorUserId: reply.userId,
        actorName: reply.userName,
        actorPhoto: reply.userPhoto,
        commentText: reply.content,
        postId,
        commentId,
        actionUrl: `/post/${encodeURIComponent(postId)}?comment=${encodeURIComponent(commentId)}`,
      });
    } catch (mentionError) {
      console.error('Error creating reply mention notifications:', mentionError);
    }

    return comments;
  } catch (error) {
    console.error('Error adding reply:', error);
    throw error;
  }
};

export const addReaction = async (
  postId: string,
  userId: string,
  type: string,
  userName: string,
  userPhoto?: string
) => {
  try {
    const postRef = doc(db, 'posts', postId);
    const postDoc = await getDoc(postRef);
    
    if (!postDoc.exists()) {
      throw new Error('Post not found');
    }

    const post = postDoc.data();
    const existingUserReactions = (post.reactions || []).filter(
      (r: any) => r.userId === userId
    );
    const hasSameReaction = existingUserReactions.some((r: any) => r.type === type);

    if (hasSameReaction) {
      // Clicking the currently selected reaction removes it.
      await updateDoc(postRef, {
        reactions: (post.reactions || []).filter((r: any) => r.userId !== userId)
      });
    } else {
      const reaction = Object.fromEntries(
        Object.entries({
          type,
          userId,
          userName,
          userPhoto: userPhoto ?? undefined,
          timestamp: new Date().toISOString(),
        }).filter(([_, value]) => value !== undefined)
      );
      const reactionsWithoutUser = (post.reactions || []).filter((r: any) => r.userId !== userId);
      await updateDoc(postRef, {
        reactions: [...reactionsWithoutUser, reaction]
      });

      // Create notification for post author if it's not their own reaction
      if (post.authorId && post.authorId !== userId) {
        try {
          await addNotification(post.authorId, 'new_reaction', {
            postId: postId,
            reactorName: userName,
            reactorPhoto: userPhoto ?? undefined,
            reactionType: type,
            postMediaType: post.mediaType || post.media?.[0] ? 'photo' : undefined,
            actionUrl: `/post/${encodeURIComponent(postId)}`
          });
          console.log('✅ Reaction notification created for user:', post.authorId);
        } catch (notificationError) {
          console.error('Error creating reaction notification:', notificationError);
          // Don't fail the reaction creation if notification fails
        }
      }
    }

    const updatedDoc = await getDoc(postRef);
    return updatedDoc.data()?.reactions || [];
  } catch (error) {
    console.error('Error updating reaction:', error);
    throw error;
  }
};

export const editPost = async (postId: string, content: string, mediaUrl?: string, mediaType?: string) => {
  try {
    const postRef = doc(db, 'posts', postId);
    await updateDoc(postRef, {
      content,
      mediaUrl,
      mediaType,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error editing post:', error);
    throw error;
  }
};

export const deletePost = async (postId: string) => {
  try {
    await deleteDoc(doc(db, 'posts', postId));
  } catch (error) {
    console.error('Error deleting post:', error);
    throw error;
  }
};